import { useEffect, useState, type RefObject } from 'react';

/**
 * The table's scroll affordance state, driving the mobile chrome: the header's scrolled
 * shadow (`atTop`), the search block's retraction (`direction`) and the bottom overflow
 * shadow (`hasMoreBelow`). Three independent booleans rather than one mode, because the
 * design treats them independently — the header shadow must stay while the search block
 * is retracted, and neither implies the other.
 *
 * "Chrome" is the UI-design term (framing elements around the content — headers, shadows,
 * toolbars — as opposed to the content itself), nothing to do with the browser.
 */
export interface TableScrollChrome {
    /** True while the container is at (or rubber-banded above) its very top. */
    atTop: boolean;
    /**
     * The last committed scroll direction, or null before any meaningful movement.
     * Resets to null at the top, so a fresh descent must re-earn the threshold.
     */
    direction: 'up' | 'down' | null;
    /** True while more content lies below the viewport — false at the bottom or when nothing overflows. */
    hasMoreBelow: boolean;
}

/** The at-rest state: what a freshly opened (or non-scrolling) table reports. */
export const DEFAULT_TABLE_SCROLL_CHROME: TableScrollChrome = Object.freeze({
    atTop: true,
    direction: null,
    hasMoreBelow: false,
});

/** The slice of chrome the header cares about; the panel keeps `hasMoreBelow` to itself. */
export type TableHeaderChrome = Pick<TableScrollChrome, 'atTop' | 'direction'>;

/**
 * Movement must accumulate this far in one direction before a direction commits. Touch
 * scrolling jitters by a pixel or two around a resting finger, and committing on raw
 * deltas made the search block flap open and shut on that noise.
 */
const DIRECTION_THRESHOLD_PX = 12;

/** Keep the previous direction; refresh only the positional booleans. */
const KEEP = 'keep';

/**
 * Overscroll (iOS rubber-banding) reports scrollTop below 0 at the top and beyond the
 * range at the bottom. Clamping before any delta math means a top bounce still reads as
 * "at top" (no shadow flicker) and a bottom rebound cannot fabricate an upward scroll
 * that pops the search block open.
 */
function clampScrollTop(el: HTMLElement): number {
    return Math.min(Math.max(el.scrollTop, 0), Math.max(el.scrollHeight - el.clientHeight, 0));
}

/**
 * Watches the table's scroll container and reports {@link TableScrollChrome}.
 *
 * Follows the StatsBar affordance pattern (passive scroll listener + ResizeObserver →
 * booleans) rather than an IntersectionObserver sentinel: the observer covers content
 * growth (load more, expanding a POI) and container resizes (the search block retracting)
 * without a scroll event, and resize updates never invent a direction.
 *
 * State commits are gated on an actual boolean transition — per-pixel scroll events
 * return the previous state object untouched, so scrolling causes no re-renders beyond
 * the transitions themselves.
 *
 * Pass `enabled: false` where the chrome is unused (desktop, closed table): nothing is
 * attached and the at-rest default is returned.
 */
export function useTableScrollChrome(ref: RefObject<HTMLDivElement | null>, enabled: boolean): TableScrollChrome {
    const [chrome, setChrome] = useState<TableScrollChrome>(DEFAULT_TABLE_SCROLL_CHROME);

    useEffect(() => {
        const el = ref.current;
        if (!enabled || el === null) return;

        let lastY = clampScrollTop(el);
        let accumulated = 0;

        const commit = (direction: TableScrollChrome['direction'] | typeof KEEP) => {
            // The rule sees the mount-time sync call below; that one commit is deliberate — the
            // container may already overflow (or sit mid-scroll) the moment the hook enables, and
            // only a state read of the element can say so. Updater-form + bail-out keeps it cheap.
            // eslint-disable-next-line react-x/set-state-in-effect
            setChrome((previous) => {
                const y = clampScrollTop(el);
                const next: TableScrollChrome = {
                    atTop: y <= 0,
                    direction: direction === KEEP ? previous.direction : direction,
                    // > 1, not > 0: fractional scroll positions on zoomed/high-DPI screens
                    // leave a sub-pixel remainder at the true bottom.
                    hasMoreBelow: el.scrollHeight - el.clientHeight - y > 1,
                };
                return next.atTop === previous.atTop &&
                    next.direction === previous.direction &&
                    next.hasMoreBelow === previous.hasMoreBelow
                    ? previous
                    : next;
            });
        };

        const handleScroll = () => {
            const y = clampScrollTop(el);
            const delta = y - lastY;
            lastY = y;

            if (y <= 0) {
                accumulated = 0;
                commit(null);
                return;
            }
            // A reversal starts the count afresh — twelve pixels down then two up is a
            // downward scroll, not ten pixels of downward credit.
            if (delta !== 0 && Math.sign(delta) !== Math.sign(accumulated)) {
                accumulated = 0;
            }
            accumulated += delta;

            if (accumulated > DIRECTION_THRESHOLD_PX) {
                commit('down');
            } else if (accumulated < -DIRECTION_THRESHOLD_PX) {
                commit('up');
            } else {
                commit(KEEP);
            }
        };

        // Observing the first child (the table's own wrapper) alongside the container
        // catches content-height changes — the container's box does not change when rows
        // load in, so observing it alone would miss them.
        const resizeObserver = new ResizeObserver(() => {
            commit(KEEP);
        });
        resizeObserver.observe(el);
        if (el.firstElementChild !== null) {
            resizeObserver.observe(el.firstElementChild);
        }

        el.addEventListener('scroll', handleScroll, { passive: true });

        // Sync the positional booleans to wherever the container already is — it may be
        // mid-scroll when the hook enables (rotating to mobile with the table open).
        commit(KEEP);

        return () => {
            el.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
    }, [enabled, ref]);

    // While disabled the internal state may be stale from an earlier enabled spell; the
    // constant default also keeps desktop renders referentially stable.
    return enabled ? chrome : DEFAULT_TABLE_SCROLL_CHROME;
}
